import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Link, useNavigate } from "react-router-dom";
import { getItem, clearStorage } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { notificationsBaseUrl } from "../../../utils/urls";
import { useFacilityStore } from "../../../app/z-store/store";

function Header() {
  const navigate = useNavigate();
  const [userName, setFullname] = useState("");
  const [userEmail, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [visibleChangePassword, setVisibleChangePassword] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get facilityId from Zustand store
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  const HandleLogOut = async () => {
    await clearStorage();
    navigate("/");
  };

  const getCurrentUser = async () => {
    try {
      // Primary source: Check for APPUSER which contains the main user data after login
      const appUser = await getItem("APPUSER");

      if (appUser && appUser.user) {
        // Extract userId from APPUSER.user.userId
        if (appUser.user.userId) {
          setUserId(appUser.user.userId);
        }

        // Extract user display information
        if (appUser.user.email) {
          setEmail(appUser.user.email);
        }

        if (appUser.user.fullName) {
          setFullname(appUser.user.fullName);
        } else if (appUser.user.email) {
          // Fallback to email prefix if no fullName
          setFullname(appUser.user.email.split("@")[0]);
        }

        // If we have all the data from APPUSER, return early
        if (appUser.user.userId && appUser.user.email) {
          return;
        }
      }

      // Fallback 1: Get userId directly from localStorage
      const userIdFromStorage = await getItem("userId");

      if (userIdFromStorage && !userId) {
        setUserId(userIdFromStorage);
      }

      // Fallback 2: Get user data from persist:root for display purposes
      const persistRoot = await getItem("persist:root");

      if (
        persistRoot &&
        persistRoot.authenticationReducer &&
        (!userEmail || !userName)
      ) {
        try {
          const authData = JSON.parse(persistRoot.authenticationReducer);

          if (authData.loginUserName && !userEmail) {
            setEmail(authData.loginUserName);
            setFullname(authData.loginUserName.split("@")[0]);
          }
        } catch (parseError) {
          console.error("Error parsing auth data:", parseError);
        }
      }

      // Fallback 3: Check for rememberedUser
      if (!userEmail || !userName) {
        const rememberedUser = await getItem("rememberedUser");

        if (rememberedUser && rememberedUser.userName) {
          setEmail(rememberedUser.userName);
          setFullname(rememberedUser.userName.split("@")[0]);
        }
      }
    } catch (err) {
      console.error("Error getting current user:", err);
    }
  };

  const fetchUnreadNotifications = async () => {
    if (!userId || !selectedFacilityId) {
      return;
    }

    try {
      setLoading(true);

      const url = `${notificationsBaseUrl}/get_all_unread/${userId}/${selectedFacilityId}`;
      const response = await makeRequest2(url, "GET");

      // Handle the response structure - based on your API response
      let fetchedNotifications = [];
      if (response?.success && response?.data) {
        // Your API returns {success: true, data: [...notifications]}
        fetchedNotifications = Array.isArray(response.data)
          ? response.data
          : [response.data];
      } else if (response?.data?.notifications) {
        fetchedNotifications = Array.isArray(response.data.notifications)
          ? response.data.notifications
          : [response.data.notifications];
      } else if (response?.notifications) {
        fetchedNotifications = Array.isArray(response.notifications)
          ? response.notifications
          : [response.notifications];
      } else if (Array.isArray(response)) {
        fetchedNotifications = response;
      }

      setNotifications(fetchedNotifications);

      const count = fetchedNotifications.length;
      setUnreadCount(count);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Don't show error to user for notifications, just log it
    } finally {
      setLoading(false);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    if (!selectedFacilityId) {
      return;
    }

    try {
      const url = `${notificationsBaseUrl}/mark/${selectedFacilityId}`;

      // Simple payload - server handles all date logic
      const payload = {
        notificationId: notificationId,
      };

      const response = await makeRequest2(url, "PATCH", payload);

      if (response) {
        // Refresh notifications after marking as read
        fetchUnreadNotifications();
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    // Mark all notifications as read
    for (const notification of notifications) {
      await markNotificationAsRead(notification._id);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes} min ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    }
  };

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (userId && selectedFacilityId) {
      fetchUnreadNotifications();

      // Set up polling to refresh notifications every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadNotifications();
      }, 30000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [userId, selectedFacilityId]);

  return (
    <header className="pc-header">
      <div className="header-wrapper">
        <div className="me-auto pc-mob-drp">
          <ul className="list-unstyled">
            <li className="pc-h-item pc-sidebar-collapse">
              <Link to={"#"} className="pc-head-link ms-0" id="sidebar-hide">
                <i className="ti ti-menu-2"></i>
              </Link>
            </li>
            <li className="pc-h-item pc-sidebar-popup">
              <Link to="#" className="pc-head-link ms-0 mobile-collapse">
                <i className="ti ti-menu-2"></i>
              </Link>
            </li>
          </ul>
        </div>
        <div className="ms-auto">
          <ul className="list-unstyled">
            {/* Notifications Dropdown */}
            <li className="dropdown pc-h-item">
              <Link
                className="pc-head-link dropdown-toggle arrow-none me-0"
                data-bs-toggle="dropdown"
                to={"#"}
                role="button"
                aria-haspopup="false"
                aria-expanded="false"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <svg className="pc-icon">
                  <use xlinkHref="#custom-notification"></use>
                </svg>
                {unreadCount > 0 && (
                  <span className="badge bg-success pc-h-badge">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <div
                className={`dropdown-menu dropdown-notification dropdown-menu-end pc-h-dropdown ${showNotifications ? "show" : ""}`}
              >
                <div className="dropdown-header d-flex align-items-center justify-content-between">
                  <h5 className="m-0">Notifications</h5>
                  {unreadCount > 0 && (
                    <button
                      className="btn btn-link btn-sm p-0"
                      onClick={markAllAsRead}
                      disabled={loading}
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div
                  className="dropdown-body text-wrap header-notification-scroll position-relative"
                  style={{
                    maxHeight: `calc(100vh - 215px)`,
                    overflowY: "scroll",
                  }}
                >
                  {loading ? (
                    <div className="text-center p-3">
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="text-center p-3 text-muted">
                      <i
                        className="ti ti-bell-off mb-2"
                        style={{ fontSize: "2rem" }}
                      ></i>
                      <p className="mb-0">No unread notifications</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-span">Unread Notifications</p>
                      {notifications.map((notification) => (
                        <div key={notification._id} className="card mb-2">
                          <div className="card-body">
                            <div className="d-flex">
                              <div className="flex-shrink-0">
                                <svg className="pc-icon text-primary">
                                  <use xlinkHref="#custom-bell"></use>
                                </svg>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <div className="d-flex justify-content-between align-items-start">
                                  <span className="text-sm text-muted">
                                    {formatTimeAgo(notification.dateSent)}
                                  </span>
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() =>
                                      markNotificationAsRead(notification._id)
                                    }
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: "0.25rem 0.5rem",
                                    }}
                                  >
                                    Mark Read
                                  </button>
                                </div>
                                <h6 className="text-body mb-2">Notification</h6>
                                <p className="mb-0 text-sm">
                                  {notification.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="text-center py-2">
                    <button
                      className="btn btn-link text-danger p-0"
                      onClick={markAllAsRead}
                      disabled={loading}
                    >
                      Clear all Notifications
                    </button>
                  </div>
                )}
              </div>
            </li>

            <li className="dropdown pc-h-item header-user-profile">
              <Link
                className="pc-head-link dropdown-toggle arrow-none me-0"
                data-bs-toggle="dropdown"
                to={"#"}
                role="button"
                aria-haspopup="false"
                data-bs-auto-close="outside"
                aria-expanded="false"
              >
                <img
                  src="/assets/images/user/avatar-2.jpg"
                  alt="user-image"
                  className="user-avtar"
                />
              </Link>
              <div className="dropdown-menu dropdown-user-profile dropdown-menu-end pc-h-dropdown">
                <div className="dropdown-header d-flex align-items-center justify-content-between">
                  <h5 className="m-0">Profile</h5>
                </div>
                <div className="dropdown-body">
                  <div
                    className="profile-notification-scroll position-relative"
                    style={{ maxHeight: `calc(100vh - 225px)` }}
                  >
                    <div className="d-flex mb-1">
                      <div className="flex-shrink-0">
                        <img
                          src="/assets/images/user/avatar-2.jpg"
                          alt="user-image"
                          className="user-avtar wid-35"
                        />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-1">{userName}</h6>
                        <span>{userEmail}</span>
                      </div>
                    </div>
                    <hr className="border-secondary border-opacity-50" />

                    <p className="text-span">Manage</p>

                    <Link to="/app/settings" className="dropdown-item">
                      <span>
                        <svg className="pc-icon text-muted me-2">
                          <use href="#custom-setting-2"></use>
                        </svg>
                        <span>Settings</span>
                      </span>
                    </Link>

                    <hr className="border-secondary border-opacity-50" />
                    <div className="d-grid mb-3">
                      <button
                        className="btn btn-primary"
                        onClick={HandleLogOut}
                      >
                        <svg className="pc-icon me-2">
                          <use href="#custom-logout-1-outline"></use>
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <Dialog
        header="Change Password"
        visible={visibleChangePassword}
        style={{ width: "50vw" }}
        onHide={() => {
          if (!visibleChangePassword) return;
          setVisibleChangePassword(false);
        }}
      >
        <form>
          <div className="form-group">
            <label>Current Password:</label>
            <input className="form-control mt-2" type="password" required />
          </div>
          <div className="form-group mt-2">
            <label>New Password:</label>
            <input className="form-control mt-2" type="password" required />
          </div>
          <div className="form-group mt-2">
            <label>Confirm Password:</label>
            <input className="form-control mt-2" type="password" required />
          </div>
          <div className="text-end mt-4">
            <button type="submit" className="btn btn-outline-secondary">
              Clear
            </button>
            &nbsp;
            <button type="submit" className="btn btn-primary">
              Submit
            </button>
          </div>
        </form>
      </Dialog>
    </header>
  );
}

export default Header;
